<template>
  <v-container
    v-if="$can('browse-logistics')"
    fluid
  >
    <div class="headline mb-6">
      Equipment Logistics Panel
    </div>

    <v-row>
      <v-col
        cols="3"
      >
        <div style="position: sticky; top: 20px">
          <v-subheader class="justify-space-between pr-0">
            Filters
            <v-btn
              v-if="isFiltered"
              text
              color="red"
              @click="clearFilters"
            >
              <v-icon
                small
                left
              >
                close
              </v-icon>
              Clear
            </v-btn>
          </v-subheader>

          <v-autocomplete
            v-model="filtersDirty.contractor_id"
            solo
            label="Commissioning Yard"
            :items="contractorsCommissioningYards"
            :loading="contractors.loading"
            item-text="name"
            item-value="id"
            dense
            hide-details
            clearable
            class="mb-2"
          />

          <v-autocomplete
            v-model="filtersDirty.supplier_id"
            solo
            label="Supplier"
            :items="contractorsSuppliers"
            :loading="contractors.loading"
            item-text="name"
            item-value="id"
            dense
            hide-details
            clearable
            class="mb-2"
          />

          <v-text-field
            v-model="filtersDirty.label"
            solo
            dense
            label="Equipment Label"
            hide-details
            clearable
            autocomplete="no"
          />

          <v-subheader>
            Equipment Status
          </v-subheader>

          <button-group
            v-model="filtersDirty.ordered"
            :items="[ { value: true, text: 'Ordered' }, { value: false, text: 'Not Ordered' } ]"
            dense
            button-class="text-none"
          />

          <v-divider class="my-2" />

          <button-group
            v-model="filtersDirty.installed"
            :items="[ { value: true, text: 'Installed' }, { value: false, text: 'Not Installed' } ]"
            dense
            button-class="text-none"
          />

          <v-subheader>
            Yacht
          </v-subheader>

          <v-text-field
            v-model="filtersDirty.yacht_id"
            solo
            dense
            type="number"
            label="Yacht ID"
            hide-details
            clearable
            class="mb-2"
            autocomplete="no"
          />
          <v-text-field
            v-model="filtersDirty.yacht_name"
            solo
            dense
            label="Yacht Name"
            hide-details
            clearable
            class="mb-2"
            autocomplete="no"
          />

          <v-autocomplete
            v-model="filtersDirty.country_id"
            solo
            hide-details
            clearable
            label="Country"
            dense
            :items="countries.data"
            :loading="countries.loading"
            item-value="id"
            item-text="name"
            autocomplete="off"
            class="mb-2"
          />
          <v-text-field
            v-model="filtersDirty.ex_factory_year"
            solo
            type="number"
            label="Ex Factory Year"
            dense
            hide-details
            clearable
            class="mb-2"
          />
          <v-text-field
            v-model="filtersDirty.delivery_year"
            solo
            type="number"
            label="Delivery Year"
            dense
            hide-details
            clearable
            class="mb-2"
          />
        </div>
      </v-col>
      <v-col cols="13">
        <div>
          <pagination
            :from="equipmentPacks.meta.from"
            :to="equipmentPacks.meta.to"
            :total="equipmentPacks.meta.total"
            :last-page="equipmentPacks.meta.last_page"
            :page.sync="pagination.page"
            class="align-center"
          />

          <div
            v-if="equipmentPacks.selected.length > 0"
            class="caption pl-2"
          >
            <v-row
              no-gutters
              class="flex-nowrap"
              align="center"
            >
              <v-col
                class="shrink mr-4 text-no-wrap"
              >
                <strong>{{ equipmentPacks.selected.length }}</strong> selected
              </v-col>
              <v-col>
                <v-btn
                  rounded
                  text
                  class="caption text-none ma-0"
                  color="primary darken-2"
                  :loading="generateOrders.loading"
                  @click.stop="generateAllOrdersAndSend()"
                >
                  <v-icon
                    left
                    small
                  >
                    mail
                  </v-icon>
                  Send Order
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </div>

        <v-data-table
          v-model="equipmentPacks.selected"
          :headers="headers"
          :items="equipmentPacks.data"
          :loading="equipmentPacks.loading"
          :page.sync="pagination.page"
          :sort-by.sync="sorting.sortBy"
          :sort-desc.sync="sorting.desc"
          :server-items-length="equipmentPacks.meta.total"
          hide-default-footer
          show-select
          class="elevation-5"
        >
          <template v-slot:item="{ item: equipmentPack, isSelected, select }">
            <tr>
              <td>
                <v-checkbox
                  :input-value="isSelected"
                  primary
                  hide-details
                  class="ma-0"
                  @click.stop="select(!isSelected)"
                />
              </td>
              <td class="font-weight-bold">
                <div>
                  <a
                    href="#"
                    @click.stop="showEquipmentPackItems(equipmentPack)"
                  >
                    {{ equipmentPack.label }}
                  </a>
                </div>
                <div class="font-weight-light grey--text text--darken-2">
                  {{ equipmentPack.category.name }}
                </div>
              </td>
              <td style="max-width: 200px">
                <div
                  v-if="!equipmentPack.yacht.name"
                  class="grey--text text--lighten-1"
                >
                  <router-link :to="{ name: 'yachtDelivery', params: { id: equipmentPack.yacht.id } }">
                    #{{ equipmentPack.yacht.id }} No Name
                  </router-link>
                </div>
                <div v-else>
                  <router-link :to="{ name: 'yachtDelivery', params: { id: equipmentPack.yacht.id } }">
                    #{{ equipmentPack.yacht.id }} {{ equipmentPack.yacht.name }}
                  </router-link>
                </div>
                <div
                  v-if="!equipmentPack.yacht.model.full_label"
                  class="grey--text text--lighten-1"
                >
                  #{{ equipmentPack.yacht.model.id }}
                </div>
                <div
                  v-else
                  class="font-weight-light grey--text text--darken-2 text-truncate"
                >
                  #{{ equipmentPack.yacht.model.id }} &middot; {{ equipmentPack.yacht.model.full_label }}
                </div>
              </td>
              <td style="max-width: 200px;">
                <div>
                  {{ installedByData[equipmentPack.installed_by].label }}
                </div>
                <div
                  v-if="equipmentPack.contractor"
                  class="font-weight-light grey--text text--darken-2 text-truncate"
                >
                  {{ equipmentPack.contractor.name }}
                </div>
              </td>
              <td>
                {{ equipmentPack.yacht.ex_factory_date | moment('D MMM YYYY') }}
              </td>
              <td>
                <div v-if="equipmentPack.supplier">
                  {{ equipmentPack.supplier.name }}
                </div>
                <div
                  v-else
                  class="grey--text font-weight-light"
                >
                  N/A
                </div>
              </td>
              <td class="text-no-wrap">
                <div
                  v-if="equipmentPack.yacht.country"
                  style="max-width: 110px"
                  class="text-truncate"
                >
                  <flag
                    v-if="equipmentPack.yacht.country"
                    :iso="equipmentPack.yacht.country.code"
                    class="mr-1"
                    :squared="false"
                  />
                  <span
                    v-if="equipmentPack.yacht.destination_base"
                  >
                    {{ equipmentPack.yacht.destination_base.name }}
                  </span>
                </div>
                <div
                  v-else
                  class="grey--text text--lighten-1"
                >
                  N/A
                </div>
              </td>
              <td
                class="pa-0"
                style="width: 1px"
              >
                <v-btn
                  :text="!equipmentPack.ordered"
                  small
                  rounded
                  :color="!equipmentPack.ordered ? 'grey' : 'success'"
                  :disabled="!$can('set-eqpack-ordered-status', 'Yacht')"
                  class="text-capitalize ma-0"
                  @click="toggleEquipmentPackOrdered(equipmentPack, !equipmentPack.ordered)"
                >
                  <v-icon
                    left
                    small
                  >
                    {{ !equipmentPack.ordered ? 'close' : 'check' }}
                  </v-icon>
                  {{ !equipmentPack.ordered ? 'Not Ordered' : 'Ordered' }}
                </v-btn>
              </td>
              <td
                class="pa-0"
                style="width: 1px"
              >
                <v-btn
                  :text="!equipmentPack.installed"
                  small
                  :color="!equipmentPack.installed ? 'grey' : 'success'"
                  rounded
                  :disabled="!$can('set-eqpack-installed-status', 'Yacht')"
                  class="text-capitalize ma-0"
                  @click="toggleEquipmentPackInstalled(equipmentPack, !equipmentPack.installed)"
                >
                  <v-icon
                    left
                    small
                  >
                    {{ !equipmentPack.installed ? 'close' : 'check' }}
                  </v-icon>
                  {{ !equipmentPack.installed ? 'Not Installed' : 'Installed' }}
                </v-btn>
              </td>
              <td>
                <v-btn
                  text
                  color="grey darken-1"
                  small
                  rounded
                  class="text-none"
                  @click="openOrderPreview(equipmentPack)"
                >
                  <v-icon
                    left
                    small
                  >
                    fa fa-clipboard-list
                  </v-icon>
                  Order
                </v-btn>
              </td>
            </tr>
          </template>
          <template v-slot:no-data>
            <div class="text-center">
              There are no equipment packs at the moment.
            </div>
          </template>
        </v-data-table>

        <pagination
          :from="equipmentPacks.meta.from"
          :to="equipmentPacks.meta.to"
          :total="equipmentPacks.meta.total"
          :last-page="equipmentPacks.meta.last_page"
          :page.sync="pagination.page"
          class="mt-1 align-center"
        />
      </v-col>
    </v-row>

    <yacht-equipment-items-dialog
      v-if="equipmentPackItemsDialog.currentPack"
      :pack="equipmentPackItemsDialog.currentPack"
      :visible.sync="equipmentPackItemsDialog.visible"
    />

    <new-document-dialog
      key="order_logistics"
      :yacht-id="generateOrder.yachtId"
      :content="generateOrder.html"
      :template-id="generateOrder.currentId"
      :document-type="generateOrder.type"
      :read-only="generateOrder.readOnly"
      :visible.sync="generateOrder.dialog"
      @document:created="documentCreated"
    />

    <send-documents-dialog
      :visible.sync="generateOrders.sendDialog"
      :attachments="generateOrders.generatedDocuments"
      :supplier="generateOrders.supplier"
      :equipment-pack="equipmentPacks.selected.length > 0 ? equipmentPacks.selected[0] : undefined"
      @order:sent="fetchEquipmentPacks(); equipmentPacks.selected = []"
    />
  </v-container>
</template>

<script>
import Yacht from '@/models/Yacht'
import YachtEquipmentPack from '@/models/YachtEquipmentPack'
import paginationMixin from '@/mixins/paginationMixin'
import filterableMixin from '@/mixins/filterableMixin'
import YachtEquipmentItemsDialog from '@/components/business/yachts/YachtEquipmentItemsDialog'
import NewDocumentDialog from '@/components/business/yachts/NewDocumentDialog'
import DocumentTemplate from '@/models/DocumentTemplate'
import Document from '@/models/Document'
import ButtonGroup from '@/components/helpers/ButtonGroup'
import { cloneDeep } from 'lodash'
import Contractor from '@/models/Contractor'
import File from '@/models/File'
import SendDocumentsDialog from '@/components/business/yachts/SendDocumentsDialog'

const filters = {
  ordered: false,
  installed: undefined,
  country_id: undefined,
  contractor_id: undefined,
  supplier_id: undefined,
  yacht_id: undefined,
  yacht_name: undefined,
  label: undefined,
  ex_factory_year: undefined
}

export default {
    name: 'EquipmentLogisticsIndex',
    components: {
      SendDocumentsDialog,
      ButtonGroup,
      NewDocumentDialog,
      YachtEquipmentItemsDialog
    },
    mixins: [paginationMixin, filterableMixin],
    data() {
      return {
        equipmentPacks: {
          data: [],
          loading: false,
          meta: {},
          selected: []
        },
        pagination: {
          page: 1,
          perPage: 50
        },
        sorting: {
          sortBy: 'ex_factory_date',
          desc: false
        },
        filtersClean: cloneDeep(filters),
        filtersDirty: cloneDeep(filters),
        equipmentPackItemsDialog: {
          currentPack: undefined,
          visible: false
        },
        generateOrder: {
          yachtId: undefined,
          html: null,
          dialog: false,
          loadingPreview: false,
          submitting: false,
          type: DocumentTemplate.TYPE_CONTRACT,
          readOnly: false,
          selectTypeModal: false
        },
        generateOrders: {
          loading: false,
          generatedDocuments: [],
          supplier: null,
          sendDialog: false
        }
      }
    },
    computed: {
      headers: function () {
        return [
          { text: 'Equipment Pack', value: 'label', sortable: false },
          { text: 'Yacht', value: 'yacht_name', sortable: false },
          { text: 'Installed By', value: 'hull_number', sortable: false },
          { text: 'Ex Factory', value: 'ex_factory_date', sortable: true },
          { text: 'Supplier', value: 'delivery_year', sortable: true },
          { text: 'Destination', value: 'destination_base.name', sortable: false },
          { text: 'Ordered', value: 'ordered', sortable: false },
          { text: 'Installed', value: 'installed', sortable: false },
          { sortable: false }
        ]
      },
      countries() {
        return this.$store.getters['settings/charteringCountries']
      },
      manufacturers() {
        return this.$store.getters['settings/yachtManufacturers']
      },
      contractors() {
        return this.$store.getters['settings/contractors']
      },
      contractorsCommissioningYards() {
        return this.contractors.data.filter(c => c.type === Contractor.TYPE_YARD)
      },
      contractorsSuppliers() {
        return this.contractors.data.filter(c => c.type === Contractor.TYPE_SUPPLIER)
      }
    },
    created() {
      this.yachtModelSpecs = Yacht
      this.installedByData = YachtEquipmentPack.installedByData
      this.documentType = DocumentTemplate.TYPE_OFFER

      this.fetchEquipmentPacks()
    },
    methods: {
      async fetchEquipmentPacks() {
        this.equipmentPacks.loading = true

        let queryParams = {
          ...this.paginationQueryParams,
          ...this.filterQueryParams,
          yacht_status: [Yacht.STATUS_PLANNED, Yacht.STATUS_DELIVERY],
          installed_by: [YachtEquipmentPack.INSTALLED_BY_CONTRACTOR, YachtEquipmentPack.INSTALLED_BY_NAVIGARE]
        }

        try {
          let result = await YachtEquipmentPack.api.fetchPivot(queryParams)

          this.equipmentPacks.data = result.data
          this.equipmentPacks.meta = result.meta
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.equipmentPacks.loading = false
        }
      },
      onPaginationUpdate() {
        this.fetchEquipmentPacks()
      },
      onFiltersUpdate() {
        this.fetchEquipmentPacks()
      },
      showEquipmentPackItems(equipmentPack) {
        this.equipmentPackItemsDialog.currentPack = equipmentPack
        this.equipmentPackItemsDialog.visible = true
      },
      async toggleEquipmentPackOrdered(equipmentPack, ordered) {
        try {
          await Yacht.api.toggleEquipmentPackOrdered(equipmentPack.yacht.id, equipmentPack.id, ordered)

          equipmentPack.ordered = !equipmentPack.ordered

          this.equipmentPacks.data.splice(this.equipmentPacks.data.findIndex(ep => ep.id === equipmentPack.id), equipmentPack)
        } catch (e) {
          this.$store.dispatch('alert', {
            color: 'error',
            text: 'Could not toggle status'
          })
          throw e
        }
      },
      async toggleEquipmentPackInstalled(equipmentPack, installed) {
        try {
          await Yacht.api.toggleEquipmentPackInstalled(equipmentPack.yacht.id, equipmentPack.id, installed)

          equipmentPack.installed = !equipmentPack.installed

          this.equipmentPacks.data.splice(this.equipmentPacks.data.findIndex(ep => ep.id === equipmentPack.id), equipmentPack)
        } catch (e) {
          this.$store.dispatch('alert', {
            color: 'error',
            text: 'Could not toggle status'
          })
          throw e
        }
      },
      async openOrderPreview(equipmentPack) {
        this.generateOrder.loadingPreview = true

        let data = {
          yacht_id: equipmentPack.yacht.id,
          type: DocumentTemplate.TYPE_ORDER,
          document_template_id: undefined,
          yacht_equipment_pack_id: equipmentPack.id
        }

        try {
          let html = await Document.api.createPreview(data)

          this.generateOrder.currentId = undefined
          this.generateOrder.yachtId = equipmentPack.yacht.id
          this.generateOrder.html = html
          this.generateOrder.type = DocumentTemplate.TYPE_ORDER
          this.generateOrder.readOnly = false
          this.generateOrder.dialog = true
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })

          throw e
        } finally {
          this.generateOrder.loadingPreview = false
        }
      },
      documentCreated(document) {
        this.generateOrder.dialog = false
        File.api.openDocument(document.file.id)
      },
      async generateAllOrdersAndSend() {
        this.generateOrders.loading = true

        try {
          const generatedDocuments = []

          for (const ep of this.equipmentPacks.selected) {
            let doc = await Document.api.generateDocument({
              html: await Document.api.createPreview({
                yacht_id: ep.yacht.id,
                type: DocumentTemplate.TYPE_ORDER,
                yacht_equipment_pack_id: ep.id
              }),
              type: DocumentTemplate.TYPE_ORDER,
              yacht_id: ep.yacht.id
            })

            doc.equipment_pack = ep

            generatedDocuments.push(doc)
          }

          this.generateOrders.supplier = this.equipmentPacks.selected[0].supplier
          this.generateOrders.generatedDocuments = generatedDocuments

          this.generateOrders.sendDialog = true
        } catch (e) {
          this.$store.dispatch('alert', {
            text: 'Something went wrong while generating PDF orders',
            color: 'error'
          })
          throw e
        } finally {
          this.generateOrders.loading = false
        }
      }
    }
  }
</script>

<style lang="scss">
  .filtered {
    opacity: 0.4;

    &.selected {
      opacity: 1;
    }
  }
</style>
